/**
 * Curated groups. Creating one is a management act — it appears in everyone's rail — so
 * the form is behind collections:manage and absent, not disabled, without it.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get, post } from "../lib/api";
import { usePermission } from "../hooks/useSession";
import type { CollectionSummary } from "../lib/types";
import { Shell } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Chip, Empty, Failed, Panel } from "../components/ui";
import { ago } from "../lib/format";

export function Collections() {
  const client = useQueryClient();
  const navigate = useNavigate();
  // usePermission() takes no argument; it returns the predicate, invoked below.
  const can = usePermission();
  const canManage = can("collections:manage");
  const [name, setName] = useState("");
  const listing = useQuery({
    queryKey: ["collections", "all"],
    queryFn: () => get<{ collections: CollectionSummary[] }>("/v1/collections?archived=true"),
  });
  const create = useMutation({
    mutationFn: () => post<CollectionSummary>("/v1/collections", { name }),
    onSuccess: () => {
      setName("");
      void client.invalidateQueries({ queryKey: ["collections"] });
    },
  });
  const rows = listing.data?.collections ?? [];

  return (
    <Shell fill crumb={<span className="text-text">collections</span>}>
      <Panel
        fill
        title="collections"
        actions={
          canManage && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim() !== "") create.mutate();
              }}
              className="flex gap-2"
            >
              <input
                aria-label="new collection name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="new collection"
                className="border border-hair bg-transparent px-2 py-1 font-mono text-[11.5px]"
              />
              <Button type="submit" disabled={create.isPending}>create</Button>
            </form>
          )
        }
      >
        {listing.isError ? (
          <Failed error={listing.error} what="collections" />
        ) : !listing.isPending && rows.length === 0 ? (
          <Empty
            title="no collections yet"
            hint="A collection is a group you curate on purpose. A tag is the ambient one — type it onto a document instead."
          />
        ) : (
          <DataTable
            fill
            table="collections"
            loading={listing.isPending}
            rows={rows}
            rowKey={(row) => row.id}
            onRowClick={(row) => navigate(`/collections/${row.id}`)}
            columns={[
              { key: "name", header: "name", width: 240, render: (row) => row.name },
              { key: "description", header: "description", width: 320, cellClass: "text-dim", render: (row) => row.description ?? "—" },
              { key: "count", header: "documents", width: 110, align: "end", cellClass: "tnum font-mono", render: (row) => row.document_count },
              { key: "created", header: "created", width: 120, cellClass: "text-[12px] text-dim", render: (row) => ago(row.created_at) },
              { key: "archived", header: "", width: 100, render: (row) => (row.archived_at ? <Chip tone="mut">archived</Chip> : null) },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
